import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import {
  BookingStep,
  BookingFormData,
  BookingContextValue,
  CreateBookingResponse,
  CreateBookingRequest,
} from '../types/booking.types';
import { bookingsApi } from '../api/bookingsApi';

const STORAGE_KEY = 'massatherapy_booking_draft';

const initialFormData: BookingFormData = {
  serviceId: '',
  therapistId: null,
  date: null,
  startTime: '',
  endTime: '',
  customerNotes: '',
  paymentMethod: 'STRIPE_CARD',
};

const BookingContext = createContext<BookingContextValue | undefined>(undefined);

export function useBooking(): BookingContextValue {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within BookingProvider');
  }
  return context;
}

interface BookingProviderProps {
  children: ReactNode;
}

function loadFromStorage(): { step: BookingStep; formData: BookingFormData } | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    // Restore Date object
    if (parsed.formData?.date) {
      parsed.formData.date = new Date(parsed.formData.date);
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveToStorage(step: BookingStep, formData: BookingFormData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, formData }));
  } catch {
    // Ignore storage errors
  }
}

function clearStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

export function BookingProvider({ children }: BookingProviderProps) {
  const [currentStep, setCurrentStep] = useState<BookingStep>(1);
  const [formData, setFormData] = useState<BookingFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [bookingResult, setBookingResult] = useState<CreateBookingResponse | null>(null);

  // Load saved booking on mount
  useEffect(() => {
    const saved = loadFromStorage();
    if (saved) {
      setCurrentStep(saved.step);
      setFormData(saved.formData);
    }
  }, []);

  // Save to storage on changes
  useEffect(() => {
    if (formData.serviceId) {
      saveToStorage(currentStep, formData);
    }
  }, [currentStep, formData]);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => {
      const next = (prev + 1) as BookingStep;
      return next > 5 ? 5 : next;
    });
  }, []);

  const previousStep = useCallback(() => {
    setCurrentStep((prev) => {
      const next = (prev - 1) as BookingStep;
      return next < 1 ? 1 : next;
    });
  }, []);

  const goToStep = useCallback((step: BookingStep) => {
    setCurrentStep(step);
  }, []);

  const updateFormData = useCallback((data: Partial<BookingFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setSubmitError(null);
  }, []);

  const submitBooking = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (!formData.date || !formData.startTime || !formData.endTime) {
        throw new Error('Please select a date and time');
      }

      const dateStr = formData.date.toISOString().split('T')[0];
      if (!dateStr) {
        throw new Error('Invalid date format');
      }

      const request: CreateBookingRequest = {
        serviceId: formData.serviceId,
        therapistId: formData.therapistId || undefined,
        date: dateStr,
        startTime: formData.startTime,
        endTime: formData.endTime,
        customerNotes: formData.customerNotes,
        paymentMethod: formData.paymentMethod,
      };

      const result = await bookingsApi.create(request);
      setBookingResult(result);
      clearStorage();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to create booking. Please try again.';
      setSubmitError(message);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);

  const resetBooking = useCallback(() => {
    setCurrentStep(1);
    setFormData(initialFormData);
    setIsSubmitting(false);
    setSubmitError(null);
    setBookingResult(null);
    clearStorage();
  }, []);

  const value: BookingContextValue = {
    currentStep,
    formData,
    isSubmitting,
    submitError,
    bookingResult,
    nextStep,
    previousStep,
    goToStep,
    updateFormData,
    submitBooking,
    resetBooking,
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}
